/* import __COLOCATED_TEMPLATE__ from './api-docs-side-drawer.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */

import Component from '@glimmer/component';
import { action } from '@ember/object';
import type TaskSuggestion from 'embercom/models/content-service/task-suggestion';
import { tracked } from '@glimmer/tracking';
import type IntlService from 'embercom/services/intl';
import { inject as service } from '@ember/service';
import type NotificationsService from 'embercom/services/notifications-service';
import type RouterService from '@ember/routing/router-service';
import copyToClipboard from 'copy-to-clipboard';
// @ts-expect-error
import { sanitizeHtml } from '@intercom/pulse/lib/sanitize';

interface Signature {
  Args: {
    suggestion: TaskSuggestion;
    isOpen: boolean;
    onClose: () => void;
  };
}

export default class ApiDocsSideDrawer extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare notificationsService: InstanceType<typeof NotificationsService>;
  @service declare router: RouterService;
  @tracked openSectionId: string | null = null;
  @tracked isHoveringSampleRequest = false;
  @tracked isHoveringCopyButton = false;
  @tracked isHoveringSampleResponse = false;
  @tracked isHoveringCopyResponseButton = false;
  @tracked isImplementationEffortOpen = false;

  @action
  closeSideDrawer() {
    this.args.onClose();
  }

  @action
  onOpenSectionChange(sectionId: string) {
    this.openSectionId = sectionId;
  }

  @action
  toggleImplementationEffort() {
    this.isImplementationEffortOpen = !this.isImplementationEffortOpen;
  }

  get formattedOverview() {
    let text = this.intl.t(
      'automation.analyze.task-suggestions.api-docs-side-drawer.overview.engineer_review',
    );
    return sanitizeHtml(text);
  }

  get formattedApiDocumentationDescription() {
    let text = this.intl.t(
      'automation.analyze.task-suggestions.api-docs-side-drawer.api_documentation.description',
    );
    return sanitizeHtml(text);
  }

  get formattedActionDescription() {
    if (!this.args.suggestion.actionDescription) {
      return '';
    }
    return sanitizeHtml(this.args.suggestion.actionDescription);
  }

  get formattedActionParams() {
    if (!this.hasActionParams) {
      return '{}';
    }
    return JSON.stringify(this.args.suggestion.actionParams, null, 2);
  }

  get hasActionParams() {
    return Object.keys(this.args.suggestion.actionParams).length > 0;
  }

  get hasMockResponse() {
    return Object.keys(this.args.suggestion.mockResponse).length > 0;
  }

  get formattedMockResponse() {
    if (!this.hasMockResponse) {
      return '{}';
    }
    return JSON.stringify(this.args.suggestion.mockResponse, null, 2);
  }

  get whatItDoesActionDescription() {
    let formatted = this.formattedActionDescription;
    if (!formatted) {
      return '';
    }
    let description = this.args.suggestion.actionDescription;
    // Look for "What it does:" followed by content until we hit a delimiter:
    // - HTML closing tag (</p>)
    // - Triple dash separator (---)
    // - Another section heading (When to use:, How it works:, etc.)
    // - End of string
    let whatItDoesMatch = description.match(
      /What it does:\s*(.+?)(?=\s*(?:<\/p>|---|When to use:|$))/is,
    );
    return whatItDoesMatch ? whatItDoesMatch[1].trim() : '';
  }

  get whenToUseActionDescription() {
    let formatted = this.formattedActionDescription;
    if (!formatted) {
      return '';
    }
    let description = this.args.suggestion.actionDescription;
    // Look for "When to use:" followed by content until we hit a delimiter:
    // - HTML closing tag (</p>)
    // - Triple dash separator (---)
    // - Another section heading (What it does:, How it works:, etc.)
    // - End of string
    let whenToUseMatch = description.match(
      /When to use:\s*(.+?)(?=\s*(?:<\/p>|---|What it does:|$))/is,
    );
    return whenToUseMatch ? whenToUseMatch[1].trim() : '';
  }

  get implementationEffortLevel() {
    let score = this.args.suggestion.implementationDifficultyScore;
    if (score === null || score === undefined) {
      return null;
    }

    if (score <= 2) {
      return this.intl.t('automation.analyze.task-suggestions.effort-score.low');
    } else if (score <= 4) {
      return this.intl.t('automation.analyze.task-suggestions.effort-score.medium');
    } else {
      return this.intl.t('automation.analyze.task-suggestions.effort-score.high');
    }
  }

  get formattedImplementationDifficultyReasoning() {
    if (!this.args.suggestion.implementationDifficultyReasoning) {
      return '';
    }
    return sanitizeHtml(this.args.suggestion.implementationDifficultyReasoning);
  }

  @action
  copyToClipboardWithNotification(text: string, successKey: string, errorKey: string) {
    try {
      // Use the copy-to-clipboard library which handles fallbacks automatically
      let success = copyToClipboard(text);

      if (success) {
        this.notificationsService.notifyConfirmation(this.intl.t(successKey));
      } else {
        throw new Error('Copy to clipboard failed');
      }
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      this.notificationsService.notifyError(this.intl.t(errorKey));
    }
  }

  @action
  onCopyActionParams() {
    this.copyToClipboardWithNotification(
      this.formattedActionParams,
      'automation.analyze.task-suggestions.api-docs-side-drawer.data_inputs.copied',
      'automation.analyze.task-suggestions.api-docs-side-drawer.data_inputs.copy_failed',
    );
  }

  @action
  onHoverSampleRequest() {
    this.isHoveringSampleRequest = true;
  }

  @action
  onLeaveSampleRequest() {
    this.isHoveringSampleRequest = false;
  }

  @action
  onCopyButtonHover() {
    this.isHoveringCopyButton = true;
  }

  @action
  onCopyButtonLeave() {
    this.isHoveringCopyButton = false;
  }

  @action
  onCopyMockResponse() {
    this.copyToClipboardWithNotification(
      this.formattedMockResponse,
      'automation.analyze.task-suggestions.api-docs-side-drawer.example_response.copied',
      'automation.analyze.task-suggestions.api-docs-side-drawer.example_response.copy_failed',
    );
  }

  @action
  onHoverSampleResponse() {
    this.isHoveringSampleResponse = true;
  }

  @action
  onLeaveSampleResponse() {
    this.isHoveringSampleResponse = false;
  }

  @action
  onCopyResponseButtonHover() {
    this.isHoveringCopyResponseButton = true;
  }

  @action
  onCopyResponseButtonLeave() {
    this.isHoveringCopyResponseButton = false;
  }

  @action
  copyShareLink() {
    let currentURL = new URL(window.location.href);

    // Add query params for the suggestion and API docs drawer
    currentURL.searchParams.set('suggestionId', this.args.suggestion.id);
    currentURL.searchParams.set('showApiDocs', 'true');

    // Determine job type based on HTTP method
    let jobId = this.args.suggestion.isDataGap ? 'dataGaps' : 'actionGaps';
    currentURL.searchParams.set('jobId', jobId);

    let shareableURL = currentURL.toString();

    this.copyToClipboardWithNotification(
      shareableURL,
      'automation.analyze.task-suggestions.api-docs-side-drawer.overview.link_copied',
      'automation.analyze.task-suggestions.api-docs-side-drawer.overview.link_copy_failed',
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::FinAiAgent::Analyze::TaskSuggestions::ApiDocsSideDrawer': typeof ApiDocsSideDrawer;
    'reporting/automation/fin-ai-agent/analyze/task-suggestions/api-docs-side-drawer': typeof ApiDocsSideDrawer;
  }
}
